import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ChamadoFileUpload from "@/components/ui/chamado-file-upload";
import { Textarea } from "@/components/ui/textarea";
import {
  fetchMensagensChamado,
  gravarMensagemChamado,
} from "@/services/chamados";
import { getAttachments } from "@/services/clientAreaService";
import { AnexoChamado, SacChamado, SacMensagem } from "@/types/Chamados";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ArrowLeft,
  Check,
  FileText,
  Loader2,
  MessageSquare,
  User,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";

interface ChamadoDetailProps {
  chamado: SacChamado;
  onBack: () => void;
  onFinalizarChamado: () => void;
  isEncerrandoChamado?: boolean;
}

export default function ChamadoDetail({
  chamado,
  onBack,
  onFinalizarChamado,
  isEncerrandoChamado = false,
}: ChamadoDetailProps) {
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [response, setResponse] = useState("");
  const [mensagens, setMensagens] = useState<SacMensagem[]>([]);
  const [anexosChamado, setAnexosChamado] = useState<AnexoChamado[]>([]);
  const [anexosMensagens, setAnexosMensagens] = useState<
    Record<string, AnexoChamado[]>
  >({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const loadMensagens = useCallback(async () => {
    try {
      setLoading(true);

      const data = await fetchMensagensChamado(chamado.sac_codigo);
      setMensagens(data);

      try {
        const anexosChamadoData = await getAttachments(
          "sac",
          chamado.sac_codigo,
        );
        setAnexosChamado(anexosChamadoData);
      } catch {
        setAnexosChamado([]);
      }

      const anexosPromises = data.map(async (msg) => {
        try {
          const anexos = await getAttachments("smg", msg.smg_codigo);
          return { smg_codigo: msg.smg_codigo, anexos };
        } catch {
          console.log(
            `Sem anexos na mensagem ${msg.smg_codigo} ou erro ao carregar`,
          );
          return { smg_codigo: msg.smg_codigo, anexos: [] };
        }
      });

      const anexosResults = await Promise.all(anexosPromises);
      const anexosMap: Record<string, AnexoChamado[]> = {};
      anexosResults.forEach((result) => {
        anexosMap[result.smg_codigo] = result.anexos;
      });
      setAnexosMensagens(anexosMap);
    } catch (error) {
      console.error("Erro ao carregar mensagens:", error);
    } finally {
      setLoading(false);
    }
  }, [chamado.sac_codigo]);

  useEffect(() => {
    loadMensagens();
  }, [loadMensagens]);

  const handleSubmitResponse = async () => {
    if (!response.trim()) return;

    try {
      setSending(true);
      await gravarMensagemChamado(
        chamado.sac_codigo,
        response,
        "1",
        uploadedFiles.length > 0 ? uploadedFiles : undefined,
      );

      setResponse("");
      setUploadedFiles([]);
      setShowResponseForm(false);

      await loadMensagens();
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      alert("Erro ao enviar mensagem. Tente novamente.");
    } finally {
      setSending(false);
    }
  };

  const getFileName = (filePath: string) => {
    return filePath.split("/").pop() || filePath;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="text-n-green hover:bg-n-green/10"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          <span className="hidden sm:inline">Voltar para lista</span>
          <span className="sm:hidden">Voltar</span>
        </Button>
      </div>

      <Card>
        <CardHeader className="p-2 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="text-base sm:text-lg text-n-green">
              Detalhes do Chamado
            </CardTitle>
            <span
              className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium self-start ${
                chamado.sac_parecer_final
                  ? "bg-green-100 text-green-700 border-green-200"
                  : "bg-amber-100 text-amber-700 border-amber-200"
              }`}
            >
              {chamado.sac_parecer_final ? "Concluído" : "Em Andamento"}
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm mb-4">
            <div className="min-w-0">
              <p className="text-xs text-gray-500 mb-1">Protocolo</p>
              <p className="font-semibold text-gray-800 truncate">
                #{chamado.sac_codigo}
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-500 mb-1">Data de Abertura</p>
              <p className="font-semibold text-gray-800 truncate">
                {format(
                  new Date(chamado.sac_data_abertura),
                  "dd/MM/yyyy 'às' HH:mm",
                  { locale: ptBR },
                )}
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-500 mb-1">Cotação</p>
              <p className="font-semibold text-gray-800 truncate">
                #{chamado.cve_codigo}
              </p>
            </div>
          </div>

          <div className="pt-4 border-t">
            <p className="text-xs text-gray-500 mb-2">Descrição do Problema</p>
            <p className="text-sm text-gray-700 break-words">
              {chamado.sac_descricao}
            </p>
          </div>

          {anexosChamado.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs text-gray-500 mb-2">Anexos do Chamado</p>
              <div className="space-y-2">
                {anexosChamado.map((anexo) => (
                  <div
                    key={anexo.anx_codigo}
                    className="flex items-center gap-2 text-sm min-w-0"
                  >
                    <FileText className="w-4 h-4 text-n-text-neutral shrink-0" />
                    <a
                      href={`${import.meta.env.VITE_API_URL}/${
                        anexo.anx_arquivo
                      }`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-n-green hover:text-n-green/80 truncate"
                    >
                      {getFileName(anexo.anx_arquivo)}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
        <Button
          onClick={() => setShowResponseForm(!showResponseForm)}
          disabled={chamado.sac_parecer_final !== null || isEncerrandoChamado}
          className="flex items-center justify-center gap-2 h-10 w-full sm:w-auto"
        >
          <MessageSquare className="h-4 w-4" />
          <span className="hidden sm:inline">Responder interação</span>
          <span className="sm:hidden">Responder</span>
        </Button>
        <Button
          onClick={onFinalizarChamado}
          disabled={chamado.sac_parecer_final !== null || isEncerrandoChamado}
          className="flex items-center justify-center gap-2 h-10 w-full sm:w-auto"
        >
          {isEncerrandoChamado ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="hidden sm:inline">Encerrando...</span>
              <span className="sm:hidden">Encerrando...</span>
            </>
          ) : (
            <>
              <Check className="h-4 w-4" />
              <span className="hidden sm:inline">Encerrar chamado</span>
              <span className="sm:hidden">Encerrar</span>
            </>
          )}
        </Button>
      </div>

      {showResponseForm && (
        <Card className="border">
          <CardHeader className="p-2 sm:p-6 pb-3">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base text-n-green">
                Nova Mensagem
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowResponseForm(false);
                  setResponse("");
                  setUploadedFiles([]);
                }}
                className="shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-2 sm:p-6 space-y-3">
            <Textarea
              placeholder="Digite sua mensagem..."
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <ChamadoFileUpload onFilesChange={setUploadedFiles} />
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
              <Button
                variant="green"
                onClick={() => {
                  setShowResponseForm(false);
                  setResponse("");
                  setUploadedFiles([]);
                }}
                disabled={sending}
                className="h-10 w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                variant="green"
                onClick={handleSubmitResponse}
                disabled={!response.trim() || sending}
                className="h-10 w-full sm:w-auto"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  "Enviar mensagem"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-0 bg-transparent shadow-none">
        <CardHeader className="p-2 sm:p-6">
          <CardTitle className="text-base sm:text-lg text-n-green">
            Status do seu chamado:
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-n-green" />
              <span className="ml-2 text-gray-600">
                Carregando mensagens...
              </span>
            </div>
          ) : mensagens.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              Nenhuma mensagem ainda.
            </p>
          ) : (
            <div>
              {mensagens.map((mensagem, index) => {
                const isCliente = mensagem.smg_cliente_enviou === "1";

                return (
                  <div key={mensagem.smg_codigo} className="relative pt-1">
                    <div className="flex gap-2 sm:gap-4">
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div className="relative z-10 min-w-7 min-h-7 sm:w-[30px] sm:h-[30px] rounded-full bg-zinc-200 border-2 border-zinc-300 flex items-center justify-center">
                          <Check
                            className={`h-3 w-3 sm:h-4 sm:w-4 ${
                              isCliente ? "text-blue-800" : "text-n-green"
                            }`}
                          />
                        </div>
                        {index !== mensagens.length - 1 && (
                          <div className="w-0.5 h-full bg-transparent border-l-2 border-dashed border-gray-300 mt-1" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0 pb-3 border-t pt-2 mt-3">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3 gap-2">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0">
                            <div
                              className={`inline-flex items-center gap-2 sm:gap-4 rounded-full p-[4px] self-start ${
                                isCliente ? "bg-n-beige" : "bg-n-green-s-light"
                              }`}
                            >
                              <div
                                className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center shrink-0 ${
                                  isCliente ? "bg-n-green" : "bg-n-green"
                                }`}
                              >
                                {isCliente ? (
                                  <User className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-white" />
                                ) : (
                                  <img
                                    src="/images/favicon.png"
                                    alt="Nacional GO"
                                    className="h-3 w-3 sm:h-4 sm:w-4"
                                  />
                                )}
                              </div>
                              <span
                                className={`font-semibold text-xs sm:text-sm pr-3 sm:pr-4 ${
                                  isCliente
                                    ? "text-n-text-green"
                                    : "text-n-text-green"
                                }`}
                              >
                                {isCliente ? "Você" : "Nacional"}
                              </span>
                            </div>

                            <span className="text-xs sm:text-sm font-medium text-n-text-green line-clamp-2 break-words">
                              {mensagem.smg_titulo ||
                                (isCliente
                                  ? mensagem.smg_texto.substring(0, 30) + "..."
                                  : "Resposta da empresa")}
                            </span>
                          </div>
                          <div className="flex flex-col items-start sm:items-end gap-2 shrink-0">
                            <span className="text-xs text-gray-400 whitespace-nowrap">
                              Data:{" "}
                              {format(
                                new Date(mensagem.smg_data_envio),
                                "dd/MM/yyyy",
                                { locale: ptBR },
                              )}
                            </span>
                          </div>
                        </div>
                        {/* {isUnread && (
                          <div className="mb-2">
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-n-text-green bg-n-green-h-light px-2 py-1 rounded-full">
                              Não visualizado{" "}
                              <span className="p-1 rounded-full bg-n-yellow">
                                <FiAlertTriangle className="h-3 w-3 text-n-text-green" />
                              </span>
                            </span>
                          </div>
                        )} */}
                        <p className="text-sm text-gray-800 mb-4 whitespace-pre-wrap break-words">
                          {mensagem.smg_texto}
                        </p>

                        {anexosMensagens[mensagem.smg_codigo]?.length > 0 && (
                          <div className="space-y-2 mb-2">
                            {anexosMensagens[mensagem.smg_codigo].map(
                              (anexo) => (
                                <div
                                  key={anexo.anx_codigo}
                                  className="flex items-center gap-2 text-sm min-w-0"
                                >
                                  <FileText className="w-4 h-4 text-n-text-neutral flex-shrink-0" />
                                  <a
                                    href={`${import.meta.env.VITE_API_URL}/${
                                      anexo.anx_arquivo
                                    }`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline text-n-green hover:text-n-green/80 truncate"
                                  >
                                    {getFileName(anexo.anx_arquivo)}
                                  </a>
                                </div>
                              ),
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
